import React from 'react';
import { FormattedMessage } from '../../util/reactIntl';
import { types as sdkTypes } from '../../util/sdkLoader';
import { formatMoney } from '../../util/currency';

import css from './OrderBreakdown.module.css';

const { Money } = sdkTypes;

/**
 * CL-DISC-01 §E — "revisedItemTotal = negotiatedItemTotal − discount", requirement 3's fourth row.
 *
 * From `protectedData.negotiation`, not from the line items, because the line items express the
 * same figure as three separate rows (items at list, one negative adjustment, one negative
 * discount) and re-adding them here would be a second implementation of arithmetic the server
 * already did. `LineItemSubTotalMaybe` cannot serve: it includes shipping and only renders when a
 * commission applies to the viewer (LineItemSubTotalMaybe.js:71-96).
 *
 * D13 (CL-NEG-01 post-merge, 2026-09-09) removed the dedicated discount's ENTRY POINTS, but NOT
 * the `discount` subtraction below — a pre-D13 transaction's stored discount still has to be
 * subtracted to show the correct revised total. Deliberately parked for a future standalone
 * discount feature — do not delete it as dead code just because no NEW transaction sets it.
 *
 * @component
 * @param {Object} props
 * @param {Object} [props.negotiation] - `transaction.attributes.protectedData.negotiation`
 * @param {string} props.currency
 * @param {Object} props.intl
 * @returns {JSX.Element|null}
 */
const LineItemRevisedItemTotalMaybe = props => {
  const { negotiation, currency, intl } = props;
  if (!negotiation) return null; // EC13: a BIN order has no negotiation key.

  const negotiated = negotiation.negotiatedItemTotalInSubunits;
  const discount = negotiation.discountInSubunits || 0;
  const original = negotiation.originalItemTotalInSubunits;
  if (!Number.isInteger(negotiated) || !Number.isInteger(original)) return null;

  // DISC AC3 and §F: rows render only when they carry information. Nothing was negotiated and
  // nothing was discounted ⇒ this row would repeat the item total one line above it.
  if (negotiated === original && discount === 0) return null;

  return (
    <div className={css.lineItem}>
      <span className={css.itemLabel}>
        <FormattedMessage id="OrderBreakdown.revisedItemTotal" />
      </span>
      <span className={css.itemValue}>
        {formatMoney(intl, new Money(negotiated - discount, currency))}
      </span>
    </div>
  );
};

export default LineItemRevisedItemTotalMaybe;
